/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Collapse } from 'antd';
import { Button as CrcButton, Checkbox, ConfirmationModal, Spinner } from '@crc-evans/ui-components';
import { useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';
import ProductivitySummaryBadges from './ProductivitySummaryBadges';
import ProductivityMainlinePanel, {
  invalidateDprProductivityCachesForLog,
  fetchDprProductivityLogWhenTabOpens,
} from './ProductivityMainlinePanel';
import axiosConfig from '../../services/axiosConfig';
import { isOffshoreProject } from '../../utils/projectConfig';

const { Panel } = Collapse;

const getMainlineDisplayTitle = (ml) =>
  ml.pipelineSection ||
  ml.name ||
  (ml.pipeStringId != null && ml.pipeStringId !== ''
    ? `Mainline ${ml.pipeStringId}`
    : `Mainline ${ml.id}`);

/**
 * Collapse row: mainline name + summary badges on the left; Skip mainline (+ Add Weld Details onshore) on the right.
 * Clicks on checkbox/button must not toggle the panel — only the collapse arrow expands.
 */
const ProductivityMainlineCollapseHeader = ({
  pipelineTitle,
  getPanelApi,
  summary,
  summaryLoading,
  skipMainline,
  onSkipMainlineChange,
  isOffshore = false,
}) => {
  const stopPanelToggle = (e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    if (e && typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
  };
  return (
    <div
      role="presentation"
      onClick={stopPanelToggle}
      onMouseDown={stopPanelToggle}
      className="productivity-mainline-collapse-header"
    >
      <span className="productivity-mainline-collapse-header__title">{pipelineTitle}</span>
      <div className="productivity-mainline-collapse-header__actions">
        <ProductivitySummaryBadges summary={summary} isLoading={summaryLoading} />
        <Checkbox
          label="Skip mainline"
          checked={skipMainline}
          onChange={(e) => {
            stopPanelToggle(e);
            const checked = e?.target ? e.target.checked : e;
            onSkipMainlineChange(checked);
          }}
        />
        {!isOffshore && (
          <CrcButton
            label="Add Weld Details"
            variant="primary"
            size="small"
            icon={Plus}
            onClick={(ev) => {
              stopPanelToggle(ev);
              getPanelApi()?.openAddWeld?.();
            }}
            style={{ backgroundColor: '#F43F5E', borderColor: '#F43F5E' }}
          />
        )}
      </div>
    </div>
  );
};

/**
 * One DPR log per day (`dprLogId`); productivity still edits welds per mainline (`mainlineProductivity` on save).
 * Multiple mainlines: collapsible cards (collapsed by default). All panels share the same `productivityData`
 * from the day-level DPR log GET.
 */
const ProductivityLog = ({
  productivityData,
  onSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId,
  selectedProject,
  selectedPipeline,
  isLastDprSection,
  /** All SOWs (mainlines) for the project — from DPR page pipeline list */
  projectMainlines = null,
  /** Incremented when the user opens the Productivity tab — triggers GET /dpr-productivity (once + cache for other mainlines). */
  refreshToken = 0,
  isShiftSkipped = false,
  /** @deprecated Header KPIs are driven by DPRPage for the selected pipeline. */
  onAggregatedSummaryChange = null,
}) => {
  const panelApisRef = useRef({});
  const singlePanelRef = useRef(null);
  const dispatch = useDispatch();
  const [globalSkipConfirmOpen, setGlobalSkipConfirmOpen] = useState(false);
  const [skipConfirmSowId, setSkipConfirmSowId] = useState(null);
  const [mainlineSearch, setMainlineSearch] = useState('');
  const [activeCollapseKeys, setActiveCollapseKeys] = useState([]);
  const [mainlineSummaries, setMainlineSummaries] = useState({});
  const [isGlobalSaving, setIsGlobalSaving] = useState(false);
  const [isTabFetching, setIsTabFetching] = useState(false);
  const [loadingPanelIds, setLoadingPanelIds] = useState(() => new Set());
  const mainlines = Array.isArray(projectMainlines) ? projectMainlines : [];
  const isOffshore = isOffshoreProject(selectedProject);
  const skipStatesBySection = useSelector((state) => state.dprReducer?.skipStatesBySection ?? {});

  const sortedMainlines = useMemo(
    () =>
      [...mainlines].sort((a, b) =>
        getMainlineDisplayTitle(a).localeCompare(getMainlineDisplayTitle(b), undefined, {
          sensitivity: 'base',
        })
      ),
    [mainlines]
  );

  const searchTerm = mainlineSearch.trim().toLowerCase();

  const filteredMainlines = useMemo(() => {
    if (!searchTerm) return sortedMainlines;
    return sortedMainlines.filter((ml) =>
      getMainlineDisplayTitle(ml).toLowerCase().includes(searchTerm)
    );
  }, [sortedMainlines, searchTerm]);

  const visibleMainlineIds = useMemo(
    () => new Set(filteredMainlines.map((ml) => String(ml.id))),
    [filteredMainlines]
  );

  const visibleMainlineKeys = useMemo(
    () => filteredMainlines.map((ml) => String(ml.id)),
    [filteredMainlines]
  );

  const allMainlinesProductivitySkipped = useMemo(
    () =>
      mainlines.length > 0 &&
      mainlines.every((ml) => Boolean(skipStatesBySection[ml.id]?.skipProductivity)),
    [mainlines, skipStatesBySection]
  );

  const skipConfirmMainline = useMemo(
    () => (skipConfirmSowId != null ? mainlines.find((ml) => String(ml.id) === String(skipConfirmSowId)) : null),
    [skipConfirmSowId, mainlines]
  );

  const allVisibleExpanded = useMemo(
    () =>
      visibleMainlineKeys.length > 0 &&
      visibleMainlineKeys.every((key) => activeCollapseKeys.includes(key)),
    [visibleMainlineKeys, activeCollapseKeys]
  );

  const handleMainlineSummaryChange = useCallback((mlId, summary) => {
    if (mlId == null) return;
    const key = String(mlId);
    setMainlineSummaries((prev) => {
      const existing = prev[key];
      if (
        existing &&
        existing.totalWelds === summary.totalWelds &&
        existing.acceptedCounts === summary.acceptedCounts &&
        existing.rejectedCutout === summary.rejectedCutout &&
        existing.rejectedRepair === summary.rejectedRepair &&
        existing.isLoading === summary.isLoading
      ) {
        return prev;
      }
      return { ...prev, [key]: summary };
    });
  }, []);

  const handleMainlineLoadingChange = useCallback((mlId, isLoading) => {
    if (mlId == null) return;
    const key = String(mlId);
    setLoadingPanelIds((prev) => {
      const next = new Set(prev);
      if (isLoading) next.add(key);
      else next.delete(key);
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev;
      return next;
    });
  }, []);

  const isTabLoading = isTabFetching || loadingPanelIds.size > 0;

  const handleToggleExpandAll = () => {
    if (allVisibleExpanded) {
      setActiveCollapseKeys((prev) => prev.filter((key) => !visibleMainlineKeys.includes(key)));
      return;
    }
    setActiveCollapseKeys((prev) => [...new Set([...prev, ...visibleMainlineKeys])]);
  };

  const aggregatedSummary = useMemo(() => {
    if (mainlines.length === 0) return null;

    const anyMainlineLoading = mainlines.some((ml) => {
      const summary = mainlineSummaries[String(ml.id)];
      return !summary || summary.isLoading;
    });

    const totals = mainlines.reduce(
      (acc, ml) => {
        const summary = mainlineSummaries[String(ml.id)];
        if (!summary || summary.isLoading) return acc;
        return {
          totalWelds: acc.totalWelds + (summary.totalWelds ?? 0),
          acceptedCounts: acc.acceptedCounts + (summary.acceptedCounts ?? 0),
          rejectedCutout: acc.rejectedCutout + (summary.rejectedCutout ?? 0),
          rejectedRepair: acc.rejectedRepair + (summary.rejectedRepair ?? 0),
        };
      },
      { totalWelds: 0, acceptedCounts: 0, rejectedCutout: 0, rejectedRepair: 0 }
    );

    const loadedCount = mainlines.filter((ml) => {
      const summary = mainlineSummaries[String(ml.id)];
      return summary && !summary.isLoading;
    }).length;

    return {
      ...totals,
      isLoading: loadedCount === 0 && (isTabFetching || anyMainlineLoading),
    };
  }, [mainlines, mainlineSummaries, isTabFetching]);

  const onAggregatedSummaryChangeRef = useRef(onAggregatedSummaryChange);
  onAggregatedSummaryChangeRef.current = onAggregatedSummaryChange;

  useEffect(() => {
    if (typeof onAggregatedSummaryChangeRef.current !== 'function') return;
    onAggregatedSummaryChangeRef.current(aggregatedSummary);
  }, [aggregatedSummary]);

  /** Use card layout even for a single mainline (consistent UI). */
  const useCardLayout = mainlines.length >= 1 && selectedPipeline?.id != null;

  const mainlineIdsKey = useMemo(
    () => mainlines.map((m) => m.id).filter((id) => id != null).join(','),
    [mainlines]
  );

  useEffect(() => {
    if (!dprLogId) return;

    setIsTabFetching(true);
    setMainlineSummaries({});
    setLoadingPanelIds(new Set());

    /** Panel refs attach after Collapse/header paint; retry until imperative API is reachable. */
    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 40;
    const delayMs = 75;

    const runRefresh = async () => {
      await fetchDprProductivityLogWhenTabOpens(dprLogId, shift).catch((err) => {
        console.error('GET /dpr-productivity (productivity tab load):', err);
      });
      if (cancelled) return true;

      if (!useCardLayout) {
        const fn = singlePanelRef.current?.reloadProductivityData;
        if (typeof fn !== 'function') return false;
        return (await fn()) === true;
      }
      if (mainlines.length === 0) return true;
      const apis = mainlines.map((ml) => panelApisRef.current?.[ml.id]);
      if (apis.some((api) => !api || typeof api.reloadProductivityData !== 'function')) return false;
      for (let i = 0; i < apis.length; i += 1) {
        if (cancelled) return true;
        await apis[i].reloadProductivityData();
      }
      return true;
    };

    const loop = async () => {
      await new Promise((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      try {
        while (!cancelled && attempt < maxAttempts) {
          try {
            /* eslint-disable no-await-in-loop -- sequential retries until refs exist */
            if (await runRefresh()) return;
          } catch {
            /* keep retrying */
          }
          attempt += 1;
          await new Promise((resolve) => {
            setTimeout(resolve, delayMs);
          });
        }
      } finally {
        if (!cancelled) setIsTabFetching(false);
      }
    };

    void loop();

    return () => {
      cancelled = true;
      setIsTabFetching(false);
    };
  }, [refreshToken, dprLogId, shift, useCardLayout, mainlineIdsKey]);

  const handlePerMainlineSkipChange = (mlId, checked) => {
    if (!mlId) return;
    if (!checked) {
      dispatch(updateDprSkipState(mlId, 'skipProductivity', false));
      return;
    }
    setSkipConfirmSowId(mlId);
  };

  const handleGlobalSave = async () => {
    if (isShiftSkipped) return;
    if (!dprLogId) return;
    setIsGlobalSaving(true);
    try {
      const parts = mainlines
        .map((ml) => panelApisRef.current?.[ml.id]?.getMainlineDailyWeldLogDraft?.())
        .filter(Boolean);
      const payload = {
        shift,
        dprProductivity: {
          dprLogId,
          mainlineProductivity: parts,
        },
      };
      await axiosConfig.put('/dpr-productivity', payload);
      invalidateDprProductivityCachesForLog(dprLogId, shift);
      const apis = mainlines.map((ml) => panelApisRef.current?.[ml.id]);
      if (apis[0]?.refreshFromServer) {
        await apis[0].refreshFromServer();
        for (let i = 1; i < apis.length; i += 1) {
          if (typeof apis[i]?.reloadProductivityData === 'function') await apis[i].reloadProductivityData();
        }
      }
    } finally {
      setIsGlobalSaving(false);
    }
  };

  if (!useCardLayout) {
    return (
      <ProductivityMainlinePanel
        ref={singlePanelRef}
        productivityData={productivityData}
        onSave={onSave}
        isSubmitting={isSubmitting}
        shift={shift}
        dprLogId={dprLogId}
        sowId={sowId}
        selectedProject={selectedProject}
        selectedPipeline={selectedPipeline}
        isLastDprSection={isLastDprSection}
        isShiftSkipped={isShiftSkipped}
        compactChrome={false}
      />
    );
  }

  return (
    <div className="productivity-log-root" style={{ width: '100%', paddingBottom: '72px', position: 'relative' }}>
      {isTabLoading ? (
        <div className="productivity-tab-loader" role="status" aria-live="polite" aria-label="Loading productivity data">
          <Spinner variant="inline" size="medium" />
          <span className="productivity-tab-loader__text">Loading productivity data...</span>
        </div>
      ) : null}
      <div className={isTabLoading ? 'productivity-log-content productivity-log-content--loading' : 'productivity-log-content'}>
      <div className="productivity-mainline-toolbar">
        <div className="productivity-mainline-toolbar-left">
          <div className="productivity-mainline-search">
            <Search size={14} color="#9CA3AF" style={{ flexShrink: 0 }} />
            <input
              value={mainlineSearch}
              onChange={(e) => setMainlineSearch(e.target.value)}
              placeholder="Search mainlines..."
              className="productivity-mainline-search__input"
            />
          </div>
        </div>
        <div className="productivity-mainline-toolbar-actions">
          {filteredMainlines.length > 0 ? (
            <div className="productivity-mainline-expand-btn">
              <CrcButton
                label={allVisibleExpanded ? 'Collapse all' : 'Expand all'}
                variant="secondary"
                size="small"
                icon={allVisibleExpanded ? ChevronUp : ChevronDown}
                onClick={handleToggleExpandAll}
              />
            </div>
          ) : null}
          <Checkbox
            label="Skip productivity"
            checked={allMainlinesProductivitySkipped}
            onChange={(e) => {
              const checked = e?.target ? e.target.checked : e;
              if (!checked) {
                mainlines.forEach((ml) => {
                  dispatch(updateDprSkipState(ml.id, 'skipProductivity', false));
                });
                return;
              }
              setGlobalSkipConfirmOpen(true);
            }}
          />
        </div>
      </div>

      {filteredMainlines.length === 0 ? (
        <div
          style={{
            padding: '24px 16px',
            textAlign: 'center',
            color: '#6B7280',
            fontSize: '14px',
          }}
        >
          No mainlines match your search.
        </div>
      ) : (
        <Collapse
          bordered={false}
          className="productivity-mainline-collapse"
          activeKey={activeCollapseKeys}
          onChange={(keys) => setActiveCollapseKeys(Array.isArray(keys) ? keys : [keys])}
          destroyInactivePanel={false}
        >
          {sortedMainlines.map((ml) => {
            const key = String(ml.id);
            const title = getMainlineDisplayTitle(ml);
            const isVisible = visibleMainlineIds.has(key);
            const skipMainline = Boolean(skipStatesBySection[ml.id]?.skipProductivity);

            return (
              <Panel
                key={key}
                forceRender
                className={isVisible ? undefined : 'productivity-mainline-panel-hidden'}
                header={
                  <ProductivityMainlineCollapseHeader
                    pipelineTitle={title}
                    getPanelApi={() => panelApisRef.current[ml.id]}
                    summary={mainlineSummaries[key]}
                    summaryLoading={!mainlineSummaries[key] || mainlineSummaries[key]?.isLoading}
                    skipMainline={skipMainline}
                    onSkipMainlineChange={(checked) => handlePerMainlineSkipChange(ml.id, checked)}
                    isOffshore={isOffshore}
                  />
                }
              >
                <ProductivityMainlinePanel
                  ref={(node) => {
                    if (node) panelApisRef.current[ml.id] = node;
                    else delete panelApisRef.current[ml.id];
                  }}
                  key={`${ml.id}-${shift}`}
                  productivityData={
                    productivityData && typeof productivityData === 'object' ? productivityData : {}
                  }
                  onSave={onSave}
                  isSubmitting={isSubmitting}
                  shift={shift}
                  dprLogId={dprLogId}
                  sowId={ml.id}
                  selectedProject={selectedProject}
                  selectedPipeline={ml}
                  mainlineTitle={title}
                  isLastDprSection={isLastDprSection}
                  isShiftSkipped={isShiftSkipped}
                  compactChrome
                  hideFooterActions
                  onSummaryChange={(summary) => handleMainlineSummaryChange(ml.id, summary)}
                  onLoadingChange={(loading) => handleMainlineLoadingChange(ml.id, loading)}
                />
              </Panel>
            );
          })}
        </Collapse>
      )}

      {/* Save / Save & Next — sticky at bottom (one API call for all mainlines) */}
      <div
        style={{
          position: 'sticky',
          bottom: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '24px',
          paddingTop: '16px',
          zIndex: 10,
        }}
      >
        <CrcButton
          label={
            typeof isLastDprSection === 'boolean'
              ? isLastDprSection
                ? 'Save section'
                : 'Save & Next'
              : 'Save'
          }
          variant="secondary"
          onClick={handleGlobalSave}
          disabled={isSubmitting || isGlobalSaving || isShiftSkipped}
          style={{ height: '42px', padding: '0 32px', fontSize: '14px', fontWeight: 600 }}
        />
      </div>

      <ConfirmationModal
        title="Skip productivity"
        message={`Are you sure you want to skip the Productivity log for all ${mainlines.length} sections for this ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={globalSkipConfirmOpen}
        onConfirm={() => {
          mainlines.forEach((ml) => {
            dispatch(updateDprSkipState(ml.id, 'skipProductivity', true));
          });
          setGlobalSkipConfirmOpen(false);
        }}
        onCancel={() => setGlobalSkipConfirmOpen(false)}
        size="medium"
      />

      <ConfirmationModal
        title="Skip mainline"
        message={`Are you sure you want to skip the Productivity log for mainline "${skipConfirmMainline ? getMainlineDisplayTitle(skipConfirmMainline) : ''}" for this ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={skipConfirmSowId != null}
        onConfirm={() => {
          if (skipConfirmSowId != null) {
            dispatch(updateDprSkipState(skipConfirmSowId, 'skipProductivity', true));
          }
          setSkipConfirmSowId(null);
        }}
        onCancel={() => setSkipConfirmSowId(null)}
        size="medium"
      />
      </div>
    </div>
  );
};

export default ProductivityLog;
